/* eslint-disable react/jsx-props-no-spreading */
import { faCaretLeft, faCaretDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropTypes as MicromagPropTypes } from '@micromag/core';
import { Label, Button, Link, Date, Spinner } from '@micromag/core/components';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import useStoryPublicationUnpublish from '../../hooks/useStoryPublicationUnpublish';
import * as AppPropTypes from '../../lib/PropTypes';

import { usePublicationServices } from '../../contexts/PublicationsContext';
import PublicationEmbedForm from '../forms/PublicationEmbed';

const propTypes = {
    item: AppPropTypes.storyPublication.isRequired,
    story: MicromagPropTypes.story,
    className: PropTypes.string,
    onUnpublish: PropTypes.func,
};

const defaultProps = {
    story: null,
    className: null,
    onUnpublish: null,
};

const StoryPublication = ({ item, story, onUnpublish, className }) => {
    const intl = useIntl();
    const services = usePublicationServices();
    const [detailsOpened, setDetailsOpened] = useState(false);
    const { id: storyId, title = null } = story || {};
    const {
        url = null,
        service: serviceId,
        results = {},
        published_at: publishedAt = null,
        unpublished_at: unpublishedAt = null,
        failed = false,
        failed_at: failedAt = null,
        updated_at: updatedAt = null,
        can_unpublish: canUnpublish = false,
        pending = false,
    } = item || {};
    const statusDate = failed ? failedAt : publishedAt;
    const finalDate = statusDate !== null ? statusDate : updatedAt;
    const { code = null, message = null } = results || {};
    const service = services.find((it) => it.id === serviceId) || null;
    const { name = null } = service || {};

    const { unpublish } = useStoryPublicationUnpublish(storyId);

    const [unpublishing, setUnpublishing] = useState(false);
    const onClickUnpublish = useCallback(() => {
        if (
            // eslint-disable-next-line no-alert
            window.confirm(
                intl.formatMessage({
                    defaultMessage: 'Are you sure you want to unpublish this Micromag?',
                    description: 'Unpublish confirmation',
                }),
            )
        ) {
            setUnpublishing(true);
            unpublish(item).then((newItem) => {
                if (onUnpublish !== null) {
                    onUnpublish(newItem);
                }
            });
        }
    }, [item, onUnpublish]);

    const onClickDetails = useCallback(
        () => setDetailsOpened(!detailsOpened),
        [detailsOpened, setDetailsOpened],
    );
    return (
        <li
            className={classNames([
                'list-group-item',
                'px-3',
                'py-2',
                {
                    [className]: className !== null,
                },
            ])}
        >
            <div className="row align-items-center mx-n2">
                <div className="col-auto px-2 me-auto">
                    <Label>{service !== null ? name : serviceId}</Label>
                </div>
                {pending ? <Spinner className="col-auto px-2 me-auto" /> : null}
                {url !== null ? (
                    <div className="col px-2 me-auto text-muted">
                        <Link href={url} external className="text-reset">
                            <small>{url}</small>
                        </Link>
                    </div>
                ) : null}
                {code !== null ? (
                    <div className="col px-2 me-auto">
                        <div className="text-reset">
                            <small className="text-dark">
                                <FormattedMessage
                                    defaultMessage="An error occured while publishing this Micromag"
                                    description="Publishing error"
                                />
                            </small>
                        </div>
                    </div>
                ) : null}
                <div className="col-auto px-2 text-muted">
                    {!pending && finalDate !== null && unpublishedAt === null ? (
                        <small className="">
                            <Date date={finalDate} withTime />
                        </small>
                    ) : null}
                    {unpublishedAt !== null ? (
                        <small className="">
                            <FormattedMessage
                                defaultMessage="Unpublished"
                                description="Field label"
                            />
                        </small>
                    ) : null}
                </div>
                {canUnpublish ? (
                    <div className="col-auto px-2">
                        <Button
                            size="sm"
                            outline
                            theme="secondary"
                            onClick={onClickUnpublish}
                            disabled={unpublishing}
                        >
                            <FormattedMessage
                                defaultMessage="Unpublish"
                                description="Button label"
                            />
                        </Button>
                    </div>
                ) : null}
                {!pending ? (
                    <div className="col-auto px-2">
                        <Button size="sm" className="text-light" onClick={onClickDetails}>
                            <FontAwesomeIcon icon={detailsOpened ? faCaretDown : faCaretLeft} />
                        </Button>
                    </div>
                ) : null}
            </div>
            <div
                className={classNames([
                    {
                        'd-none': !detailsOpened,
                    },
                ])}
            >
                <ul className="list-group list-group-flush mt-2">
                    {publishedAt !== null && unpublishedAt === null ? (
                        <li className="list-group-item px-0 py-2 text-muted small">
                            <div className="row align-items-center mx-n2">
                                <div className="col-3 px-2">
                                    <strong>
                                        <FormattedMessage
                                            defaultMessage="Published at"
                                            description="Field label"
                                        />
                                    </strong>
                                </div>
                                <div className="col px-2 text-light">
                                    <Date date={publishedAt} withTime />
                                </div>
                            </div>
                        </li>
                    ) : null}
                    {unpublishedAt !== null ? (
                        <li className="list-group-item px-0 py-2 text-muted small">
                            <div className="row align-items-center mx-n2">
                                <div className="col-3 px-2">
                                    <strong>
                                        <FormattedMessage
                                            defaultMessage="Unpublished at"
                                            description="Field label"
                                        />
                                    </strong>
                                </div>
                                <div className="col px-2 text-light">
                                    <Date date={unpublishedAt} withTime />
                                </div>
                            </div>
                        </li>
                    ) : null}
                    {url !== null ? (
                        <li className="list-group-item px-0 py-2 text-muted small">
                            <div className="row align-items-center mx-n2">
                                <div className="col-3 px-2">
                                    <strong>
                                        <FormattedMessage
                                            defaultMessage="URL"
                                            description="Field label"
                                        />
                                    </strong>
                                </div>
                                <div className="col px-2 text-light">
                                    <Link href={url} external className="text-reset">
                                        {url}
                                    </Link>
                                </div>
                            </div>
                        </li>
                    ) : null}
                    {serviceId === 'micromag' ? (
                        <li className="list-group-item px-0 py-2 text-muted small">
                            <div className="row align-items-center mx-n2">
                                <div className="col-3 px-2">
                                    <strong>
                                        <FormattedMessage
                                            defaultMessage="Embed"
                                            description="Field label"
                                        />
                                    </strong>
                                </div>
                                <div className="col px-2 text-light">
                                    <PublicationEmbedForm url={url} title={title} />
                                </div>
                            </div>
                        </li>
                    ) : null}
                    {code !== null ? (
                        <li className="list-group-item px-0 py-2 text-muted small">
                            <div className="row align-items-center mx-n2">
                                <div className="col px-2" title={message}>
                                    <FormattedMessage
                                        defaultMessage="An error occured while publishing this Micromag"
                                        description="Publishing error"
                                    />
                                </div>
                            </div>
                        </li>
                    ) : null}
                </ul>
            </div>
        </li>
    );
};

StoryPublication.propTypes = propTypes;
StoryPublication.defaultProps = defaultProps;

export default StoryPublication;
